import {
  Component,
  input,
  output,
  forwardRef,
  computed,
  signal,
  ChangeDetectionStrategy,
  effect,
  ChangeDetectorRef,
  ElementRef,
  NgZone,
  OnDestroy,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, FormsModule } from '@angular/forms';
import { HugeiconsIconComponent } from '@hugeicons/angular';
import { ArrowDown01Icon, Cancel01Icon, Search01Icon } from '@hugeicons/core-free-icons';

export interface SelectOption {
  value: string | number;
  label: string;
  disabled?: boolean;
}

@Component({
  selector: 'app-select',
  standalone: true,
  imports: [CommonModule, FormsModule, HugeiconsIconComponent],
  templateUrl: './select.html',
  styleUrl: './select.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => Select),
      multi: true,
    },
  ],
})
export class Select implements ControlValueAccessor, OnDestroy {
  ArrowDown01Icon = ArrowDown01Icon;
  Cancel01Icon = Cancel01Icon;
  Search01Icon = Search01Icon;

  // Inputs
  label = input<string | null>(null);
  placeholder = input<string>('Select an option');
  options = input.required<SelectOption[]>();
  disabled = input<boolean>(false);
  required = input<boolean>(false);
  error = input<string | null>(null);
  hint = input<string | null>(null);
  icon = input<any | null>(null);
  size = input<'sm' | 'md' | 'lg'>('md');
  name = input<string>('');
  searchable = input<boolean>(true);
  searchPlaceholder = input<string>('Search...');
  maxHeight = input<string>('280px');
  minWidth = input<string>('210px');
  loading = input<boolean>(false);
  getTextColor = input<((value: string | number | null) => string) | null>(null);
  readOnly = input<boolean>(false);
  clearable = input<boolean>(false);

  /** Value for two-way binding [(value)] when NOT using reactive forms. */
  value = input<string | number | null | undefined>(undefined);
  valueChange = output<string | number | null>();

  // Remote search support
  onSearch = input<((query: string) => void) | null>(null);
  searchDebounceMs = input<number>(300);

  // Outputs
  opened = output<void>();

  // Internal state
  private readonly _instanceId = `select-${Math.random().toString(36).slice(2)}`;
  private _value: string | number | null = null;
  private _disabledByControl = signal<boolean>(false);
  isOpen = signal<boolean>(false);
  searchQuery = signal<string>('');
  dropdownStyle = signal<Record<string, string>>({});
  openUpward = signal<boolean>(false);
  private _searchTimer: any = null;
  /** True after a non-empty search was performed; avoids redundant onSearch('') when dropdown closes without user search. */
  private _hasPerformedNonEmptySearch = false;

  private _onChange = (value: string | number | null) => { };
  private _onTouched = () => { };

  // Computed disabled state (combines input and control state)
  isDisabled = computed(() => this.disabled() || this._disabledByControl());

  /** Native scroll/resize handler — closes dropdown when the page scrolls while open.
   *  Ignores scroll events that originate inside the dropdown panel itself. */
  private readonly _closeOnScrollOrResize = (event: Event): void => {
    if (event.target instanceof Node && this.elementRef.nativeElement.contains(event.target)) {
      return;
    }
    this.ngZone.run(() => {
      this.closeDropdown();
      this.cdr.markForCheck();
    });
  };

  /**
   * Closes this dropdown when the user clicks outside the component.
   * Registered in capture phase so modal stopPropagation() cannot block it.
   */
  private readonly _closeOnOutsideClick = (event: MouseEvent): void => {
    if (this.isOpen() && !this.elementRef.nativeElement.contains(event.target as Node)) {
      this.ngZone.run(() => {
        this.closeDropdown();
        this.cdr.markForCheck();
      });
    }
  };

  /** Closes this dropdown when any other select/multi-select opens. */
  private readonly _closeOnOtherDropdownOpen = (event: Event): void => {
    const id = (event as CustomEvent<{ id: string }>).detail?.id;
    if (id !== this._instanceId && this.isOpen()) {
      this.ngZone.run(() => {
        this.closeDropdown();
        this.cdr.markForCheck();
      });
    }
  };

  constructor(
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef<HTMLElement>,
    private ngZone: NgZone,
  ) {
    // Sync value input to internal state
    effect(() => {
      const v = this.value();
      if (v !== undefined) {
        this._value = v ?? null;
        this.cdr.markForCheck();
      }
    });

    // Reset search when dropdown closes
    effect(() => {
      if (!this.isOpen()) {
        this.searchQuery.set('');
      }
    });

    // Debounced remote search. Only invoke onSearch('') when user had previously searched (avoids redundant API call on init/close).
    effect(() => {
      const q = this.searchQuery();
      const cb = this.onSearch();
      if (!cb) return;
      if (this._searchTimer) clearTimeout(this._searchTimer);
      this._searchTimer = setTimeout(() => {
        try {
          const trimmed = (q || '').trim();
          if (trimmed) {
            this._hasPerformedNonEmptySearch = true;
            cb(q);
          } else if (this._hasPerformedNonEmptySearch) {
            this._hasPerformedNonEmptySearch = false;
            cb(q);
          }
        } catch (e) {
          console.error('select onSearch callback error', e);
        }
      }, this.searchDebounceMs());
    });

    // Attach/detach native scroll, resize and outside-click listeners based on open state.
    // Outside-click uses capture phase so modal stopPropagation() cannot block it.
    effect(() => {
      if (this.isOpen()) {
        window.addEventListener('scroll', this._closeOnScrollOrResize, { capture: true, passive: true });
        window.addEventListener('resize', this._closeOnScrollOrResize, { passive: true });
        document.addEventListener('click', this._closeOnOutsideClick, true);
      } else {
        window.removeEventListener('scroll', this._closeOnScrollOrResize, true);
        window.removeEventListener('resize', this._closeOnScrollOrResize);
        document.removeEventListener('click', this._closeOnOutsideClick, true);
      }
    });

    // Always listen for other dropdowns opening so we can close ourselves.
    document.addEventListener('app-select-opened', this._closeOnOtherDropdownOpen);
  }

  /**
   * Calculates the fixed pixel coordinates for the dropdown panel relative to the viewport,
   * and decides whether to open upward or downward based on available space.
   */
  private calculateDropdownPosition(): void {
    const trigger = this.elementRef.nativeElement.querySelector<HTMLButtonElement>(
      'button[aria-haspopup="listbox"]',
    );
    if (!trigger) return;

    const rect = trigger.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const dropdownMaxHeight = 280;
    const gap = 4;

    const spaceBelow = viewportHeight - rect.bottom - gap;
    const shouldOpenUpward = spaceBelow < dropdownMaxHeight && rect.top > spaceBelow;

    this.openUpward.set(shouldOpenUpward);

    if (shouldOpenUpward) {
      this.dropdownStyle.set({
        position: 'fixed',
        bottom: `${viewportHeight - rect.top + gap}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: '9999',
      });
    } else {
      this.dropdownStyle.set({
        position: 'fixed',
        top: `${rect.bottom + gap}px`,
        left: `${rect.left}px`,
        width: `${rect.width}px`,
        zIndex: '9999',
      });
    }
  }

  // Filtered options (case-insensitive) for performant large lists
  filteredOptions = computed(() => {
    const opts = this.options();
    const cb = this.onSearch();

    // If remote search is enabled, don't filter locally
    if (cb) {
      return opts;
    }

    const query = this.searchQuery().trim().toLowerCase();
    if (!query) return opts;
    return opts.filter(
      (opt) =>
        String(opt.label).toLowerCase().includes(query) ||
        String(opt.value).toLowerCase().includes(query)
    );
  });

  hasNoResults = computed(() => !this.loading() && this.filteredOptions().length === 0);

  // Computed classes
  triggerClasses = computed(() => {
    const base =
      'w-full min-w-0 rounded-lg border bg-surface text-text-strong focus:outline-none focus:ring transition-colors disabled:bg-black/3 disabled:cursor-not-allowed cursor-pointer flex items-center justify-between overflow-hidden';

    const sizes = {
      sm: 'px-3 py-1.5 text-sm placeholder-text',
      md: 'px-3 py-2 text-sm placeholder-text',
      lg: 'px-4 py-2.5 text-base placeholder-text',
    };

    const hasError = this.error() !== null;
    const borderColor = hasError
      ? 'border-error focus:ring-error focus:border-error'
      : 'border-border-subtle focus:ring-border-subtle focus:border-border-subtle';

    const padding = this.icon() ? 'pl-10' : 'pl-3';
    const paddingRight = this.clearable() && this.hasValue() ? 'pr-14' : 'pr-8';

    return `${base} ${sizes[this.size()]} ${borderColor} ${padding} ${paddingRight} ${this.minWidth()}`;
  });

  get currentValue(): string | number | null {
    return this._value;
  }

  set currentValue(val: string | number | null) {
    if (val !== this._value) {
      this._value = val;
      this._onChange(val);
      this.cdr.markForCheck();
    }
  }

  writeValue(value: string | number | null): void {
    this._value = value ?? null;
    this.cdr.markForCheck();
  }

  registerOnChange(fn: (value: string | number | null) => void): void {
    this._onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this._onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    this._disabledByControl.set(isDisabled);
  }

  getDisplayText(): string {
    if (this._value === null || this._value === '') return this.placeholder();
    const option = this.options().find((opt) => opt.value === this._value);
    return option?.label ?? this.placeholder();
  }

  getDisplayTextColor(): string {
    const colorFn = this.getTextColor();
    if (colorFn && this._value !== null && this._value !== '') {
      return colorFn(this._value);
    }
    return '';
  }

  /** Returns color class for a dropdown option (when getTextColor is provided). */
  getOptionTextColor(option: SelectOption): string {
    const colorFn = this.getTextColor();
    if (colorFn) return colorFn(option.value);
    return 'text-text-strong';
  }

  /** Returns class string for the trigger display span (handles placeholder vs value color). */
  getTriggerSpanClasses(): string {
    const base = 'min-w-0 truncate text-left block';
    if (!this.hasValue()) return `${base} text-text-muted`;
    const color = this.getDisplayTextColor();
    return color ? `${base} ${color}` : `${base} text-text-strong`;
  }

  /** Returns class object for dropdown option (for ngClass). */
  getOptionClasses(option: SelectOption): { [key: string]: boolean } {
    const color = this.getOptionTextColor(option);
    return { [color]: true };
  }

  selectOption(option: SelectOption): void {
    if (this.isDisabled() || option.disabled) return;
    // Closing after a pick clears the search box; without this, the debounced onSearch effect would call
    // onSearch('') and parents often refetch page 1 even though a value was just chosen.
    this._hasPerformedNonEmptySearch = false;
    this.currentValue = option.value;
    this.valueChange.emit(option.value);
    this.closeDropdown();
  }

  clearSelection(event: Event): void {
    event.stopPropagation();
    if (this.isDisabled() || this.readOnly()) return;
    this.currentValue = null;
    this.valueChange.emit(null);
    this._onTouched();

    // If this select is remote-search driven, clearing should restore the initial list.
    const cb = this.onSearch();
    if (cb) {
      try { cb(''); } catch { /* ignore */ }
    }
  }

  hasValue(): boolean {
    return this._value !== null && this._value !== '';
  }

  isSelected(optionValue: string | number): boolean {
    return this._value === optionValue;
  }

  toggleDropdown(): void {
    if (!this.isDisabled()) {
      const wasOpen = this.isOpen();
      if (!wasOpen) {
        this.calculateDropdownPosition();
        document.dispatchEvent(new CustomEvent('app-select-opened', { detail: { id: this._instanceId } }));
      }
      this.isOpen.update((val) => !val);
      if (!wasOpen && this.isOpen()) {
        this.opened.emit();
      }
      if (!this.isOpen()) {
        this._onTouched();
      }
    }
  }

  closeDropdown(): void {
    this.isOpen.set(false);
    this._onTouched();
  }

  onSearchInput(event: Event): void {
    const target = event.target as HTMLInputElement;
    this.searchQuery.set(target.value);
  }

  onSearchKeydown(event: KeyboardEvent): void {
    if (event.key === 'Escape') {
      this.closeDropdown();
    }
  }

  ngOnDestroy(): void {
    window.removeEventListener('scroll', this._closeOnScrollOrResize, true);
    window.removeEventListener('resize', this._closeOnScrollOrResize);
    document.removeEventListener('click', this._closeOnOutsideClick, true);
    document.removeEventListener('app-select-opened', this._closeOnOtherDropdownOpen);
    if (this._searchTimer) clearTimeout(this._searchTimer);
  }
}
